import { createReducer, on } from '@ngrx/store';
import { adminActions } from '@core/actions/admin.actions';
import { authApiActions } from '@core/auth/auth.actions';

export const LoadingState = {
  idle: 'idle',
  loading: 'loading',
  loaded: 'loaded',
  error: 'error',
} as const;

export type LoadingState = (typeof LoadingState)[keyof typeof LoadingState];

/** The Admin Console user-list read. `denied` is the API refusing ListUsers, kept apart from `error`. */
export type InvestigatorsLoadState = LoadingState | 'denied';

export interface AdminUiState {
  emailTemplatesLoadState: LoadingState;
  investigatorsLoadState: InvestigatorsLoadState;
  showingAdminUiOptions: boolean;
  /**
   * M2a: the caller's application capabilities as the API decided them, for presentation only.
   * `null` means PM authority does not decide them here, so the claim-based fallback applies.
   */
  applicationCapabilities: Readonly<Record<string, boolean>> | null;
}
const initialAdminUiState: AdminUiState = {
  emailTemplatesLoadState: LoadingState.idle,
  investigatorsLoadState: LoadingState.idle,
  showingAdminUiOptions: false,
  applicationCapabilities: null,
};

export const adminUiReducer = createReducer(
  initialAdminUiState,
  on(
    adminActions.loadEmailTemplates,
    (state): AdminUiState => ({
      ...state,
      emailTemplatesLoadState: LoadingState.loading,
    })
  ),
  on(
    adminActions.emailTemplatesLoaded,
    (state): AdminUiState => ({
      ...state,
      emailTemplatesLoadState: LoadingState.loaded,
    })
  ),
  on(
    adminActions.emailTemplatesLoadFailed,
    (state): AdminUiState => ({
      ...state,
      emailTemplatesLoadState: LoadingState.error,
    })
  ),
  on(
    authApiActions.loadInvestigators,
    (state): AdminUiState => ({
      ...state,
      investigatorsLoadState: LoadingState.loading,
    })
  ),
  on(
    authApiActions.investigatorsLoaded,
    (state): AdminUiState => ({
      ...state,
      investigatorsLoadState: LoadingState.loaded,
    })
  ),
  on(
    authApiActions.investigatorsLoadFailed,
    (state, { reason }): AdminUiState => ({
      ...state,
      investigatorsLoadState: reason === 'denied' ? 'denied' : LoadingState.error,
    })
  ),
  on(
    adminActions.applicationCapabilitiesPresented,
    (state, { capabilities }): AdminUiState => ({
      ...state,
      applicationCapabilities: capabilities,
    })
  ),
  on(
    adminActions.toggleAdminUiOptions,
    (state): AdminUiState => ({
      ...state,
      showingAdminUiOptions: !state.showingAdminUiOptions,
    })
  )
);
